import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from './cn';
import { EntityChipIcon, type EntityKind } from './entityIcons';

/*
  EntityChip — каноническое представление сущности (узел, VM, локация, человек, канал, тикет):
  иконка типа + имя. Кликабельная — открывает объект во вкладке рабочего стола.
*/

export type { EntityKind };

interface EntityChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  kind: EntityKind;
  name: string;
  meta?: string;
  size?: 'sm' | 'md';
  /** Точка состояния рядом с именем (класс bg-* из статусных токенов). */
  dotClassName?: string;
}

export const EntityChip = forwardRef<HTMLButtonElement, EntityChipProps>(
  ({ kind, name, meta, size = 'sm', dotClassName, className, ...rest }, ref) => {
    return (
      <button
        ref={ref}
        type="button"
        data-slot="entity-chip"
        className={cn(
          'inline-flex max-w-full items-center gap-1.5 rounded-sm bg-muted font-medium text-fg',
          'transition-colors duration-fast hover:bg-hover focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
          size === 'sm' ? 'h-6 px-1.5 text-xs' : 'h-8 px-2 text-sm',
          className,
        )}
        {...rest}
      >
        <EntityChipIcon
          kind={kind}
          className={cn('shrink-0 text-fg-subtle', size === 'sm' ? 'size-3.5' : 'size-4')}
        />
        <span className="truncate">{name}</span>
        {meta && <span className="truncate font-normal text-fg-subtle">{meta}</span>}
        {dotClassName && <span className={cn('size-1.5 shrink-0 rounded-full', dotClassName)} aria-hidden />}
      </button>
    );
  },
);
EntityChip.displayName = 'EntityChip';
